import React from 'react'
import { FormattedMessage } from 'react-intl'
import { makeStyles } from 'tss-react/mui'
import { Link } from '@mui/material'
import messages from '@utils/messages'
import HandleMarkup from '@src/components/HandleMarkup'
import { Linq } from '@src/components/Linq'
import { sendSelectItemEvent } from '@src/utils/analytics'
import { sendPricingAvailabilityInteractionEventNewSrp } from '@src/utils/analytics/pricingAndAvailability'

const useStyles = makeStyles()((theme) => ({
  supplementary: {
    fontSize: theme.typography.pxToRem(12),
  },
  link: {
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.secondary.dark,
    cursor: 'pointer',
  },
}))

interface Supplementary {
  messageKey?: Maybe<string>
  messageValue?: Maybe<string>
  messageVariable1?: Maybe<string>
  messageVariable2?: Maybe<string>
}

interface Props {
  supplementary: Supplementary
  productName?: string
  productId?: string
  productBrand?: string
  productVariant?: string
  availabilityMessage?: string
}

const SupplementaryMessage: React.FC<Props> = ({
  supplementary,
  productName,
  productId,
  productBrand,
  productVariant,
}) => {
  const { classes } = useStyles()
  const { messageKey, messageValue, messageVariable1, messageVariable2 } =
    supplementary ?? {}
  const noMessage = messageKey && !messageValue && !messages[messageKey]
  // replacing placeholder for use with formattedMessage
  const productMessage = messageValue?.replace('%0', '{productLink}')
  const formattedMessageVariable1 = messageVariable1
    ? messageVariable1.replace(/\.$/, '').toLowerCase()
    : ''

  const getProductLink = (): JSX.Element | null => {
    return messageVariable2 && messageVariable1 ? (
      <Linq
        href={messageVariable2}
        onClick={() => {
          sendSelectItemEvent(messageVariable2, productName)
          sendPricingAvailabilityInteractionEventNewSrp({
            action: 'view alternative products',
            detail: 'check cart for availability',
            section: 'pricing card',
            component: 'right rail',
            elementType: 'link',
            elementText: formattedMessageVariable1,
            linkUrl: messageVariable2,
            product: {
              productNumber: productId,
              productVariant: productVariant,
              brandKey: productBrand,
              productName: productName,
            },
          })
        }}
      >
        <HandleMarkup value={messageVariable1} />
      </Linq>
    ) : null
  }

  if (noMessage) return null

  const renderMessage = (supplementary) => {
    switch (supplementary.messageKey) {
      case 'PT_TESTING_STUDY':
        return (
          <FormattedMessage
            {...messages[supplementary.messageKey]}
            values={{
              link: (
                <Link
                  href="https://supelco-pt.com"
                  target="_blank"
                  className={classes.link}
                >
                  <FormattedMessage {...messages.PT_TESTING_STUDY_LINK} />
                </Link>
              ),
            }}
          />
        )
      case 'REDI_DRI_RP':
        return (
          <FormattedMessage
            {...messages.REDI_DRI_RP}
            values={{
              brand: (
                <i>
                  <FormattedMessage {...messages.REAGENTPLUS} />
                </i>
              ),
            }}
          />
        )
      default:
        return (
          <FormattedMessage
            {...messages[supplementary.messageKey]}
            values={{
              messageVariable1: getProductLink() ?? messageVariable1,
            }}
          >
            {(text) => <div className={classes.supplementary}>{text}</div>}
          </FormattedMessage>
        )
    }
  }

  return (
    <div>
      {supplementary?.messageKey && messages[supplementary.messageKey] ? (
        renderMessage(supplementary)
      ) : (
        // if messageKey is not defined in the messages file render the messageValue
        <FormattedMessage
          id="DEFAULT_SUPPLEMENTARY_MESSAGE"
          defaultMessage={productMessage}
          values={{ productLink: getProductLink() ?? messageVariable1 }}
        >
          {(text) => <div className={classes.supplementary}>{text}</div>}
        </FormattedMessage>
      )}
    </div>
  )
}

export default SupplementaryMessage
